import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Heading,
  Popover,
  Spinner,
  useToast,
} from "@/components/ds";
import { CreditCardIcon } from "@/icons";
import { InfoIcon } from "@/icons/InfoIcon";
import { usePageTitle } from "../hooks/usePageTitle";
import styles from "./BillingPage.module.css";

interface Pricing {
  readonly basePriceGbp: number;
  readonly perCleanerGbp: number;
  readonly monthlyEstimateGbp: number;
}

interface BillingStateInfo {
  readonly label:
    | "trial"
    | "trial_ending"
    | "trial_expired"
    | "active"
    | "past_due"
    | "cancelled"
    | "suspended"
    | "unknown";
  readonly trialDaysRemaining: number | null;
  readonly showsBanner: boolean;
  readonly checkInBlocked: boolean;
  readonly editingBlocked: boolean;
}

interface UpcomingInvoiceLine {
  readonly description: string;
  readonly quantity: number;
  readonly unitAmountPence: number;
  readonly amountPence: number;
  readonly proration: boolean;
}

interface UpcomingInvoice {
  readonly currency: string;
  readonly totalPence: number;
  readonly subtotalPence: number;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly lines: readonly UpcomingInvoiceLine[];
}

interface Invoice {
  readonly id: string;
  readonly number: string | null;
  readonly totalPence: number;
  readonly currency: string;
  readonly status: string;
  readonly hostedInvoiceUrl: string | null;
  readonly invoicePdf: string | null;
  readonly createdAt: string;
  readonly periodStart: string;
  readonly periodEnd: string;
}

interface PaymentMethod {
  readonly brand: string | null;
  readonly last4: string | null;
}

interface BillingData {
  readonly org: {
    readonly plan: "trial" | "standard";
    readonly subscriptionStatus: string;
    readonly trialEndsAt: string;
    readonly stripeCustomerId: string | null;
    readonly stripeSubscriptionId: string | null;
    readonly billingSyncDueAt: string | null;
  };
  readonly billingState: BillingStateInfo;
  readonly cleanerCount: number;
  readonly pricing: Pricing;
  readonly upcomingInvoice: UpcomingInvoice | null;
  readonly paymentMethod: PaymentMethod | null;
  readonly invoices: readonly Invoice[];
  readonly stripeError: string | null;
}

type BadgeVariant = "success" | "warning" | "danger" | "info" | "neutral";

function stateBadge(state: BillingStateInfo): { variant: BadgeVariant; label: string } {
  switch (state.label) {
    case "trial":
    case "trial_ending": {
      const days = state.trialDaysRemaining ?? 0;
      const plural = days === 1 ? "day" : "days";
      return {
        variant: state.label === "trial_ending" ? "warning" : "info",
        label: `Trial · ${days} ${plural} left`,
      };
    }
    case "trial_expired":
      return { variant: "warning", label: "Trial ended" };
    case "active":
      return { variant: "success", label: "Active" };
    case "past_due":
      return { variant: "warning", label: "Payment overdue" };
    case "cancelled":
      return { variant: "info", label: "Cancelled" };
    case "suspended":
      return { variant: "danger", label: "Suspended" };
    case "unknown":
      return { variant: "neutral", label: "Billing not set up" };
  }
}

function formatGbp(gbp: number): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
  }).format(gbp);
}

function formatPence(pence: number, currency: string): string {
  const normalisedCurrency = currency.toUpperCase();
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: normalisedCurrency,
    minimumFractionDigits: 2,
  }).format(pence / 100);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatDateRange(startIso: string, endIso: string): string {
  return `${formatDate(startIso)} – ${formatDate(endIso)}`;
}

function sumLines(lines: readonly UpcomingInvoiceLine[]): number {
  return lines.reduce((acc, line) => acc + line.amountPence, 0);
}

function renderUpcomingBill(
  invoice: UpcomingInvoice,
  monthlyRateGbp: number,
  syncPending: boolean,
) {
  const regularLines = invoice.lines.filter((l) => !l.proration);
  const prorationLines = invoice.lines.filter((l) => l.proration);
  const regularPence = sumLines(regularLines);
  const prorationPence = sumLines(prorationLines);
  const hasAdjustment = prorationPence !== 0;
  const adjustmentIsCredit = prorationPence < 0;

  return (
    <>
      {syncPending ? (
        <div className={styles.pendingNotice}>
          <InfoIcon width={16} height={16} />
          <span>
            Your recent team changes aren't in these numbers yet. We'll update
            your next bill within 10 minutes.
          </span>
        </div>
      ) : null}
      <div className={styles.breakdown}>
        <div className={styles.breakdownRow}>
          <div>
            <div className={styles.breakdownLabel}>
              Your rate for {formatDateRange(invoice.periodStart, invoice.periodEnd)}
            </div>
          </div>
          <div className={styles.breakdownAmount}>
            {formatPence(regularPence, invoice.currency)}
          </div>
        </div>
        {hasAdjustment ? (
          <Popover
            side="top"
            align="end"
            content={
              <div className={styles.adjustmentPopover}>
                <div className={styles.adjustmentPopoverHeading}>
                  {adjustmentIsCredit ? "Credit breakdown" : "Adjustment breakdown"}
                </div>
                {prorationLines.map((line, i) => (
                  <div key={i} className={styles.adjustmentPopoverLine}>
                    <span className={styles.adjustmentPopoverDesc}>
                      {line.description}
                    </span>
                    <span className={styles.adjustmentPopoverAmount}>
                      {line.amountPence < 0 ? "−" : "+"}
                      {formatPence(Math.abs(line.amountPence), invoice.currency)}
                    </span>
                  </div>
                ))}
              </div>
            }
          >
            <button type="button" className={`${styles.breakdownRow} ${styles.clickableRow}`}>
              <div>
                <div className={styles.breakdownLabel}>
                  {adjustmentIsCredit ? "Mid-cycle credit" : "Mid-cycle adjustment"}
                  <InfoIcon className={styles.inlineInfoIcon} width={14} height={14} />
                </div>
              </div>
              <div className={styles.breakdownAmount}>
                {adjustmentIsCredit ? "−" : "+"}{formatPence(Math.abs(prorationPence), invoice.currency)}
              </div>
            </button>
          </Popover>
        ) : null}
      </div>
      <div className={styles.breakdownTotalRow}>
        <span className={styles.breakdownTotalLabel}>Total due</span>
        <span className={styles.breakdownTotalAmount}>
          {formatPence(invoice.totalPence, invoice.currency)}
        </span>
      </div>
      {hasAdjustment ? (
        <div className={styles.adjustmentNote}>
          <h3 className={styles.adjustmentNoteHeading}>
            Why is this different from usual?
          </h3>
          <p className={styles.adjustmentNoteBody}>
            Your cleaner count changed partway through this month. We're{" "}
            {adjustmentIsCredit ? "crediting" : "settling"} the difference on
            this bill. After that, you'll be back to {formatGbp(monthlyRateGbp)}{" "}
            a month.
          </p>
        </div>
      ) : null}
    </>
  );
}

function prettyStatus(status: string): string {
  if (status === "paid") return "Paid";
  if (status === "open") return "Open";
  if (status === "uncollectible") return "Uncollectible";
  if (status === "void") return "Void";
  if (status === "draft") return "Draft";
  return status;
}

function statusVariant(status: string): BadgeVariant {
  if (status === "paid") return "success";
  if (status === "open" || status === "draft") return "warning";
  if (status === "uncollectible" || status === "void") return "danger";
  return "neutral";
}

function statusCopy(state: BillingStateInfo): string {
  switch (state.label) {
    case "trial":
      return "You're on the 14-day free trial. All features are included. Add a payment method any time to keep things going after the trial ends.";
    case "trial_ending": {
      const days = state.trialDaysRemaining ?? 0;
      const plural = days === 1 ? "day" : "days";
      return `Your trial ends in ${days} ${plural}. Add a payment method now so your team can keep working without a break.`;
    }
    case "trial_expired":
      return "Your trial has ended. Add a payment method to keep using TidyRota. Everything you've created is still here.";
    case "active":
      return "Your subscription is active.";
    case "past_due":
      return "Your last payment didn't go through. Update your card to avoid any interruption.";
    case "cancelled":
      return "Your subscription is cancelled. You can restart it any time.";
    case "suspended":
      return "This account is suspended. Contact us at hello@tidyrota.com to resolve it.";
    case "unknown":
      return "Your billing is in an unexpected state. Contact us at hello@tidyrota.com and we'll sort it out.";
  }
}

export function BillingPage() {
  usePageTitle("Billing | TidyRota");
  const toast = useToast();
  const [data, setData] = useState<BillingData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<"checkout" | "portal" | null>(null);

  const load = useCallback(async () => {
    try {
      const response = await fetch("/api/billing");
      if (response.status === 403) {
        setLoadError("Only managers can view billing.");
        return;
      }
      const json = (await response.json()) as
        | { success: true; data: BillingData }
        | { success: false; error: { message: string } };
      if (!json.success) {
        setLoadError(json.error.message);
        return;
      }
      setData(json.data);
    } catch {
      setLoadError("Couldn't load billing. Check your connection and try again.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function startCheckout() {
    setActionLoading("checkout");
    try {
      const response = await fetch("/api/billing/checkout", { method: "POST" });
      const json = (await response.json()) as
        | { success: true; data: { url: string } }
        | { success: false; error: { message: string } };
      if (!json.success) {
        toast.toast("error", json.error.message);
        return;
      }
      window.location.assign(json.data.url);
    } catch {
      toast.toast("error", "Something went wrong. Please try again.");
    } finally {
      setActionLoading(null);
    }
  }

  async function openPortal() {
    setActionLoading("portal");
    try {
      const response = await fetch("/api/billing/portal", { method: "POST" });
      const json = (await response.json()) as
        | { success: true; data: { url: string } }
        | { success: false; error: { message: string } };
      if (!json.success) {
        toast.toast("error", json.error.message);
        return;
      }
      window.location.assign(json.data.url);
    } catch {
      toast.toast("error", "Something went wrong. Please try again.");
    } finally {
      setActionLoading(null);
    }
  }

  if (loadError) {
    return (
      <div className={styles.page}>
        <Alert variant="danger">{loadError}</Alert>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingRow}>
          <Spinner />
          <span>Loading billing</span>
        </div>
      </div>
    );
  }

  const { billingState, pricing, cleanerCount, upcomingInvoice, paymentMethod, invoices, stripeError, org } = data;
  const badge = stateBadge(billingState);
  const hasStripe = !!org.stripeCustomerId;
  const ctaLabel = hasStripe ? "Manage billing" : "Add payment method";
  const ctaAction = hasStripe ? openPortal : startCheckout;
  const ctaDisabled = actionLoading !== null;

  return (
    <div className={styles.page}>
      <div className={styles.heading}>
        <Heading level={1} size="h3">Billing</Heading>
        <span className={styles.subtitle}>
          Manage your TidyRota subscription and payment method
        </span>
      </div>

      <div className={`${styles.card} ${styles.statusCard}`}>
        <div className={styles.statusHeader}>
          <div>
            <div className={styles.statusTitle}>{badge.label}</div>
            <div className={styles.statusSub}>{statusCopy(billingState)}</div>
          </div>
        </div>

        <div className={styles.statSummary}>
          <div className={styles.stat}>
            <span className={styles.statLabel}>Monthly rate</span>
            <span className={styles.statValue}>{formatGbp(pricing.monthlyEstimateGbp)}</span>
            <span className={styles.statFoot}>
              £{pricing.basePriceGbp} base + {cleanerCount} × £{pricing.perCleanerGbp.toFixed(2)}
            </span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statLabel}>Billable cleaners</span>
            <span className={styles.statValue}>{cleanerCount}</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statLabel}>
              {billingState.label === "trial" || billingState.label === "trial_ending"
                ? "Trial ends"
                : billingState.label === "trial_expired"
                  ? "Trial ended"
                  : "Next charge"}
            </span>
            <span className={styles.statValue}>
              {billingState.label === "trial" || billingState.label === "trial_ending" || billingState.label === "trial_expired"
                ? formatDate(org.trialEndsAt)
                : upcomingInvoice
                  ? formatDate(upcomingInvoice.periodEnd)
                  : "—"}
            </span>
            <span className={styles.statFoot}>
              {billingState.label === "active" && upcomingInvoice
                ? formatPence(upcomingInvoice.totalPence, upcomingInvoice.currency)
                : null}
            </span>
          </div>
        </div>

        <div className={styles.actions}>
          <span className={styles.actionsHint}>
            {hasStripe
              ? "Update your card, download invoices, or cancel your subscription from Stripe's secure portal."
              : "Redirects to Stripe's secure checkout."}
          </span>
          <Button onClick={ctaAction} disabled={ctaDisabled}>
            {actionLoading === "checkout" || actionLoading === "portal" ? (
              <Spinner size="sm" />
            ) : (
              ctaLabel
            )}
          </Button>
        </div>
      </div>

      {hasStripe ? (
        <div className={styles.card}>
          <div className={styles.sectionHeader}>Next bill</div>
          {stripeError && !upcomingInvoice ? (
            <p className={styles.inlineError}>
              We couldn't reach Stripe to preview your next bill. Your charge below is estimated from your team size.
            </p>
          ) : null}
          {upcomingInvoice ? (
            renderUpcomingBill(upcomingInvoice, pricing.monthlyEstimateGbp, org.billingSyncDueAt !== null)
          ) : (
            <div className={styles.breakdown}>
              <div className={styles.breakdownRow}>
                <span className={styles.breakdownLabel}>Base</span>
                <span className={styles.breakdownAmount}>{formatGbp(pricing.basePriceGbp)}</span>
              </div>
              <div className={styles.breakdownRow}>
                <div>
                  <div className={styles.breakdownLabel}>Cleaners</div>
                  <div className={styles.breakdownMeta}>
                    {cleanerCount} × {formatGbp(pricing.perCleanerGbp)}
                  </div>
                </div>
                <span className={styles.breakdownAmount}>
                  {formatGbp(cleanerCount * pricing.perCleanerGbp)}
                </span>
              </div>
              <div className={styles.breakdownTotalRow}>
                <span className={styles.breakdownTotalLabel}>Monthly total</span>
                <span className={styles.breakdownTotalAmount}>
                  {formatGbp(pricing.monthlyEstimateGbp)}
                </span>
              </div>
            </div>
          )}
        </div>
      ) : null}

      <div className={styles.card}>
        <div className={styles.sectionHeader}>Payment method</div>
        {hasStripe && paymentMethod && paymentMethod.last4 ? (
          <div className={styles.paymentRow}>
            <div className={styles.paymentMethod}>
              <div className={styles.paymentCardIcon} aria-hidden="true">
                <CreditCardIcon width={20} height={20} />
              </div>
              <div>
                <div className={styles.paymentPrimary}>
                  {paymentMethod.brand
                    ? `${paymentMethod.brand.charAt(0).toUpperCase()}${paymentMethod.brand.slice(1)}`
                    : "Card"}{" "}
                  ending {paymentMethod.last4}
                </div>
                <div className={styles.paymentSecondary}>
                  Managed through Stripe's secure portal
                </div>
              </div>
            </div>
          </div>
        ) : (
          <EmptyState
            icon={<CreditCardIcon width={28} height={28} />}
            title={hasStripe ? "No card on file yet" : "No payment method yet"}
            description={
              hasStripe
                ? "You've been set up in Stripe but haven't added a card."
                : "You'll add your payment details during checkout. No charge until your trial ends."
            }
          />
        )}
      </div>

      <div className={styles.card}>
        <div className={styles.sectionHeader}>Invoice history</div>
        {invoices.length > 0 ? (
          <div className={styles.breakdown}>
            {invoices.map((invoice) => (
              <div key={invoice.id} className={styles.breakdownRow}>
                <div className={styles.breakdownLabel}>
                  {formatDate(invoice.createdAt)}
                </div>
                <div className={styles.invoiceRight}>
                  <span className={styles.breakdownAmount}>
                    {formatPence(invoice.totalPence, invoice.currency)}
                  </span>
                  <Badge variant={statusVariant(invoice.status)}>
                    {prettyStatus(invoice.status)}
                  </Badge>
                  {invoice.hostedInvoiceUrl ? (
                    <a
                      className={styles.invoiceLink}
                      href={invoice.hostedInvoiceUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      View
                    </a>
                  ) : (
                    <span className={styles.invoiceDim}>—</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            title="No invoices yet"
            description={
              hasStripe
                ? "Your first invoice will appear here after your trial ends."
                : "Invoices will appear here once your subscription begins."
            }
          />
        )}
      </div>
    </div>
  );
}
